/**
 * "This frame already exists in another size" — found, and then ASKED.
 *
 * A frame sold in three sizes is three products in this shop: three codes,
 * three stock positions, three prices. What they share is a model, and the
 * backend stores that as a pointer between products — never as a name.
 *
 * WHY THIS FILE IS DISCOVERY AND NOT A RULE
 * The operator types "Square Glasses 270" and picks Large. Something has to
 * notice that a Medium of the same frame is already in the shop, because
 * otherwise the only thing that happens is the slug collision the backend has
 * always reported, which tells them a name is taken and not what to do about
 * it. So the NAME is used to find a candidate and put it in front of a human.
 *
 * It is never used to create the relationship. The confirmation sends the
 * candidate's PRODUCT UUID, and the backend resolves that to the model's root.
 * Two frames that genuinely share a name and are not the same model are then
 * one "no" away from behaving exactly as they did before — which is the whole
 * reason a person is asked rather than told.
 *
 * The match is EXACT. `square-glasses-270` and its own size slugs, and nothing
 * else: no fuzzy matching, no edit distance, no prefix search. A near-miss
 * offered as a model is a wrong grouping the operator has to notice to avoid,
 * and grouping two unrelated frames is worse than asking nothing at all.
 */
import { productsApi } from "../api/products";
import { familyForCategory, isSizeOption, type SizeOption } from "../catalog/taxonomy";
import type { ProductListItem } from "../api/types";

/** The same shape the backend derives a slug in: lowercase ASCII, dashes. */
export function baseSlug(name: string): string {
  return name
    .normalize("NFKD")
    // Drop the combining marks NFKD just split off, so an accented letter
    // folds to its ASCII base the way the backend's slugify does instead
    // of turning into a separator.
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 128);
}

/** Slugs that belong to the model called [name]: its own, and its sizes'. */
export function modelSlugs(name: string): string[] {
  const base = baseSlug(name);
  if (!base) return [];
  return [base, `${base}-s`, `${base}-m`, `${base}-l`];
}

export type ModelCandidate =
  /** Nothing of this name exists; create the product the ordinary way. */
  | { kind: "clear" }
  /** This exact model already has this exact size. Not groupable. */
  | {
      kind: "duplicate";
      modelName: string;
      size: SizeOption;
      existingCode: string | null;
    }
  /** Same model, a size it does not have yet. Worth asking about. */
  | {
      kind: "candidate";
      /** Sent to the backend as model_of_product_id. Resolved there to a root. */
      modelProductId: string;
      modelName: string;
      existingSizes: SizeOption[];
      newSize: SizeOption;
    };

function sizesOf(items: readonly ProductListItem[]): SizeOption[] {
  const found = items
    .map((item) => item.frame_size)
    .filter((size): size is string => typeof size === "string" && isSizeOption(size));
  return [...new Set(found)] as SizeOption[];
}

/**
 * Does a glasses model of [name] already exist, and what should happen?
 *
 * Read through the ordinary product listing — the same search the operator
 * would run by hand — so this adds no endpoint of its own.
 */
export async function resolveModelCandidate(
  name: string,
  size: SizeOption,
  signal?: AbortSignal,
): Promise<ModelCandidate> {
  const wanted = new Set(modelSlugs(name));
  if (wanted.size === 0) return { kind: "clear" };

  const page = await productsApi.list(
    { search: name.trim(), page: 1, page_size: 100 },
    signal,
  );

  const family = page.items.filter(
    (item) => wanted.has(item.slug) && familyForCategory(item.category) === "glasses",
  );
  // One model, even if the listing happened to return several of its sizes:
  // they all report the same model_id, and the first is as good a handle as
  // any because the backend resolves whichever it is given to the root.
  const first = family[0];
  if (first === undefined) return { kind: "clear" };

  const siblings = family.filter((item) => item.model_id === first.model_id);

  const clash = siblings.find((item) => item.frame_size === size);
  if (clash) {
    return {
      kind: "duplicate",
      modelName: clash.name,
      size,
      existingCode: clash.product_code,
    };
  }

  return {
    kind: "candidate",
    modelProductId: first.product_id,
    modelName: first.name,
    existingSizes: sizesOf(siblings),
    newSize: size,
  };
}
